/**
 * O BOTÃO «SINCRONIZAR» — com o contador de pendentes sempre à vista, e os
 * conflitos (mesma ficha mudada em dois aparelhos) para a pessoa escolher.
 *
 * Um gesto só, nesta ordem (mesmo padrão da piscicultura, decisão do gestor
 * em 05/10/2026):
 *   1. SOBE o que está pendente;
 *   2. DESCE o que é novo no servidor;
 *   3. SÓ ENTÃO, e só com a fila vazia e sem conflito, oferece a versão nova
 *      do app. Trocar de código com registro pendente é o jeito de perder
 *      dado: o código novo pode ler a fila de um jeito diferente do velho.
 *      Quem decide recarregar é a pessoa — nunca no meio de uma pesagem.
 */
import { useEffect, useState, useSyncExternalStore } from 'react'
import { usarEstado, type Conflito } from '../dados/armazem'
import { recontarArquivos, usarArquivosPendentes } from '../dados/arquivos'
import { resolverConflito, sincronizar } from '../dados/fila'
import { usarExtrato } from '../dados/extrato'
import { quantosPendentes, usarPagamentos } from '../dados/pagamentos'
import { usarCatira } from '../dados/catira'
import { usarNotas } from '../dados/notas'
import { calcular } from '../dominio/contas'
import { kg, quando, reais } from '../dominio/numeros'
import type { Ficha } from '../dominio/tipos'
import { avisar, Janela, Tarja } from '../ui'

export function BarraDeSincronia() {
  const { pendentes, ultimaSync, conflitos, fichas, recusadas } = usarEstado()
  // 3E: fichas e produtores que o banco recusou de vez — fora da fila, à vista
  const nRecusadas = Object.keys(recusadas.fichas).length + Object.keys(recusadas.produtores).length
  const [indo, setIndo] = useState(false)
  const [recusas, setRecusas] = useState<string[]>([])
  const [vendoConflitos, setVendoConflitos] = useState(false)
  const versaoNova = usarVersaoNova()
  // despesas e vales do extrato (2F) e comissão e quitação lançadas sem sinal
  // (2D) também esperam o botão
  const extrato = usarExtrato()
  const pagamentos = usarPagamentos()
  // D3: movimentos da catira lançados no pasto, sem sinal
  const catira = usarCatira()
  // D4: notas fiscais lidas sem sinal (câmera, XML)
  const notas = usarNotas()
  const n = pendentes.fichas.length + pendentes.produtores.length + extrato.pendentes.length + quantosPendentes(pagamentos)
    + catira.pendentes.length + notas.pendentes.length
  // 2G: fotos e PDFs que ainda não subiram. Ficam FORA da condição da versão
  // nova: o arquivo no IndexedDB não depende da versão do app, e sem o R2
  // ligado ele espera por tempo indefinido — não pode travar a atualização.
  const arquivos = usarArquivosPendentes()
  const estado = usarEstado()
  // a conta é assíncrona (IndexedDB): refaz a cada mudança em fichas, extrato ou pagamentos
  useEffect(() => { void recontarArquivos() }, [estado, extrato, pagamentos, notas])
  const total = n + arquivos.pendentes

  async function apertar() {
    setIndo(true); setRecusas([])
    try {
      const r = await sincronizar()
      if (r.semSinal) avisar('Sem sinal — nada se perdeu. Tente de novo quando tiver internet.')
      else if (r.conflitos) avisar(`Sincronizado, com ${r.conflitos} conflito(s) para você escolher`)
      else avisar(`Sincronizado${r.subiram ? ` · ${r.subiram} enviado(s)` : ''}${r.desceram ? ` · ${r.desceram} recebido(s)` : ''}`)
      setRecusas(r.recusas)
      if (!r.semSinal) await procurarVersaoNova()
    } catch {
      avisar('Não deu para sincronizar agora. Tente de novo.')
    } finally {
      setIndo(false)
    }
  }

  return (
    <>
      <div className={`sincronia${total ? ' pendente' : ''}`}>
        <div>
          <b>{total ? `${total} para enviar` : 'Tudo enviado'}</b>
          <div className="mini-dica">{ultimaSync ? `Última sincronização: ${quando(ultimaSync)}` : 'Ainda não sincronizou neste aparelho'}</div>
          {arquivos.pendentes > 0 && (
            <div className="mini-dica">
              {arquivos.pendentes === 1 ? '1 foto ou PDF' : `${arquivos.pendentes} fotos ou PDFs`}
              {arquivos.semArmazenamento
                ? ' esperando: o armazenamento de arquivos ainda não foi ligado. Ficam guardados neste aparelho.'
                : ' entre os pendentes.'}
            </div>
          )}
          {nRecusadas > 0 && (
            <div className="mini-dica">
              {nRecusadas === 1 ? '1 ficha ou produtor recusado' : `${nRecusadas} fichas ou produtores recusados`} pelo servidor:
              {' '}fica{nRecusadas === 1 ? '' : 'm'} só neste aparelho, fora da conta de «para enviar».
            </div>
          )}
        </div>
        <button className="botao" disabled={indo} onClick={apertar}>{indo ? 'Sincronizando…' : 'Sincronizar'}</button>
      </div>
      {conflitos.length > 0 && (
        <Tarja tipo="alerta" onClick={() => setVendoConflitos(true)}>
          {conflitos.length === 1 ? 'Uma ficha foi alterada' : `${conflitos.length} fichas foram alteradas`} em outro aparelho ao mesmo tempo. As pesagens das duas já estão juntas; toque para escolher o que diverge.
        </Tarja>
      )}
      {recusas.length > 0 && <Tarja tipo="erro">{recusas.join(' · ')}</Tarja>}
      {versaoNova && !n && !conflitos.length && (
        <Tarja tipo="ok" onClick={() => location.reload()}>
          Há uma versão nova do app. Toque aqui para atualizar.
        </Tarja>
      )}
      {vendoConflitos && (
        <Janela titulo="Mesma ficha, duas versões" fechar={() => setVendoConflitos(false)}>
          <div className="mini-dica">As pesagens das duas versões já foram juntadas — nenhuma se perdeu. Falta escolher só o que foi mudado nos dois aparelhos; até lá, vale o do banco.</div>
          {conflitos.map((c, i) => {
            const doBanco = fichas.find((f) => f.id === c.local.id)
            // R1A: conflito com a lista do que diverge — escolha campo a campo
            if (c.divergencias && doBanco) return <EscolhaDoConflito key={c.local.id + c.em} c={c} indice={i} />
            return (
              <div className="cartao" key={c.local.id + c.em}>
                <div className="nome">Ficha {c.local.numero} · {c.local.produtor.nome}</div>
                <div className="linha">
                  <Versao titulo="Deste aparelho" f={c.local} />
                  {doBanco && <Versao titulo="Do banco (outro aparelho)" f={doBanco} />}
                </div>
                <button className="botao" onClick={() => { resolverConflito(i, 'minha'); avisar('Fica a deste aparelho — vai no próximo Sincronizar') }}>Ficar com a deste aparelho</button>
                <button className="botao fantasma" onClick={() => { resolverConflito(i, 'banco'); avisar('Fica a do banco') }}>Ficar com a do banco</button>
              </div>
            )
          })}
          {!conflitos.length && <div className="vazio">Nenhum conflito pendente.</div>}
          <button className="botao fantasma" onClick={() => setVendoConflitos(false)}>Fechar</button>
        </Janela>
      )}
    </>
  )
}

/**
 * R1A · Só o que diverge, campo a campo: a pessoa marca o que quer DESTE
 * aparelho; o resto fica como o do banco (que já está valendo). As pesagens
 * dos dois lados já estão juntas.
 */
function EscolhaDoConflito({ c, indice }: { c: Conflito; indice: number }) {
  const [daqui, setDaqui] = useState<Set<string>>(() => new Set())
  const lista = c.divergencias ?? []
  const alternar = (campo: string) => setDaqui((x) => { const n = new Set(x); if (n.has(campo)) n.delete(campo); else n.add(campo); return n })
  return (
    <div className="cartao">
      <div className="nome">Ficha {c.local.numero} · {c.local.produtor.nome}</div>
      <div className="mini-dica">Mudado nos dois aparelhos. Marque o que deve ficar como estava NESTE aparelho:</div>
      {lista.map((d) => (
        <label key={d.campo} className="campo" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <input type="checkbox" checked={daqui.has(d.campo)} onChange={() => alternar(d.campo)} />
          <span><b>{d.rotulo}</b><br />
            <span className="mini-dica">Deste aparelho: {d.deste}<br />Do banco: {d.doBanco}</span></span>
        </label>
      ))}
      <button className="botao" onClick={() => {
        resolverConflito(indice, [...daqui])
        avisar(daqui.size ? 'Escolha guardada — vai no próximo Sincronizar' : 'Fica o do banco')
      }}>Confirmar escolha</button>
    </div>
  )
}

function Versao({ titulo, f }: { titulo: string; f: Ficha }) {
  const r = calcular(f)
  return (
    <div className="mini-dica" style={{ border: '1px solid var(--linha)', borderRadius: 10, padding: 8 }}>
      <b>{titulo}</b><br />
      {r.cab} cab · {kg(r.kg)} kg<br />
      {r.temPreco ? `Líquido ${reais(r.liquido)}` : 'sem preço'}<br />
      {f.registros.length} registro(s) · {f.situacao}<br />
      alterada {quando(f.alteradaEm)}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Versão nova do app
// ---------------------------------------------------------------------------

let haVersaoNova = false
const ouvintesDaVersao = new Set<() => void>()
function marcarVersaoNova(): void {
  if (haVersaoNova) return
  haVersaoNova = true
  ouvintesDaVersao.forEach((f) => f())
}
function usarVersaoNova(): boolean {
  return useSyncExternalStore((f) => { ouvintesDaVersao.add(f); return () => ouvintesDaVersao.delete(f) }, () => haVersaoNova)
}

/**
 * Pergunta à Cloudflare qual versão está publicada e compara com a desta tela
 * (`__VERSAO__`, a mesma VERSAO do sw.js, gravada na montagem). Comparar o
 * número direto evita oferecer atualização a quem já abriu o app com o código
 * novo. Também pede ao navegador o service worker novo, para a recarga já
 * abrir na versão certa mesmo com sinal fraco.
 */
async function procurarVersaoNova(): Promise<void> {
  try {
    const r = await fetch('/sw.js', { cache: 'no-store' })
    const publicada = /const VERSAO = '([^']+)'/.exec(await r.text())?.[1]
    if (!r.ok || !publicada || publicada === __VERSAO__) return
    await (await navigator.serviceWorker?.getRegistration())?.update().catch(() => undefined)
    marcarVersaoNova()
  } catch {
    // sem sinal: não é erro de quem usa, não vira aviso
  }
}
